import type { ViewType } from "@/components/calendars/types.ts";
import { addDays, endOfWeek, isSameDay, isSameMonth, startOfWeek } from "date-fns";

/**
 * Whether today falls inside the window the calendar is currently showing —
 * the predicate behind every Today button's outlined/filled badge.
 *
 * Shared rather than repeated per surface: the canvas header, the day sidebar,
 * and the calendar-nav sidebar can all be on screen at once, and a Today badge
 * reading "you're on today" in one of them while another reads "you're away" is
 * worse than either answer alone. The two hand-written copies this replaces had
 * already drifted — one classified `multi-day` by its own two-day window, the
 * other silently fell through to the month check.
 *
 * `now` is injectable so tests don't have to move the system clock; production
 * callers let it default.
 */
export const isTodayVisible = (
  currentDate: Date,
  viewType: ViewType,
  now: Date = new Date(),
): boolean => {
  switch (viewType) {
    case "day":
      return isSameDay(currentDate, now);
    case "multi-day":
      // The window spans two days, so today counts as visible in either slot.
      return isSameDay(currentDate, now) || isSameDay(addDays(currentDate, 1), now);
    case "week":
      return currentDate >= startOfWeek(now) && currentDate <= endOfWeek(now);
    case "month":
      return isSameMonth(currentDate, now);
  }
};
